/* Ongoing frontend version watcher (frontend + backend always deploy together, so a frontend
 * build bump fully reflects a deploy).
 *
 * cachebuster.jsx only checks /meta.json once, at mount - a tab left open across a deploy never
 * re-checks. This polls on an interval and whenever the tab regains focus/visibility, so a stale
 * tab eventually notices. On mismatch a persistent "please reload" toast is shown; if ignored, the
 * page force-reloads after a grace period so stale clients can't linger indefinitely.
 */
import { toast } from 'react-toastify';
import i18next from 'i18next';
import packageJson from '../../package.json';
import { semverGreaterThan } from '../common/semver';

const FRONTEND_VERSION = packageJson.version;
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 min
const GRACE_PERIOD_MS = 30 * 60 * 1000; // force reload 30 min after first nag if ignored

let notified = false; // tracks "already queued/shown", independent of toast.isActive()
let forcedReloadTimer = null;
let pollTimer = null;

function refreshCacheAndReload() {
    if (window.caches) {
        caches
            .keys()
            .then((names) => Promise.all(names.map((n) => caches.delete(n))))
            .finally(() => window.location.reload(true));
    } else {
        window.location.reload(true);
    }
}

/** Shows the persistent "please reload" toast and arms the grace-period force-reload.
 * Safe to call repeatedly/from a render path - it's a no-op after the first call.
 *
 * Deliberately guarded by our own `notified` flag rather than toast.isActive(toastId):
 * before <GlobalNotifications />'s <ToastContainer /> is mounted (e.g. while still on the
 * login screen), react-toastify has no container to report an active toast against, so
 * isActive() always returns false and every call would queue another toast - all of which
 * would then render at once in a burst the moment the container finally mounts post-login. */
export function notifyNewVersionAvailable() {
    if (!forcedReloadTimer) {
        forcedReloadTimer = setTimeout(refreshCacheAndReload, GRACE_PERIOD_MS);
    }
    if (notified) return;
    notified = true;
    toast.info(i18next.t('toast.newVersionAvailableClickToReload'), {
        autoClose: false,
        closeOnClick: true,
        draggable: false,
        onClick: refreshCacheAndReload,
    });
}

function checkVersion() {
    fetch('/meta.json', { cache: 'no-store' })
        .then((response) => response.json())
        .then((meta) => {
            if (semverGreaterThan(meta.version, FRONTEND_VERSION)) {
                notifyNewVersionAvailable();
            }
        })
        .catch(() => {
            // local dev / meta.json missing -> ignore
        });
}

/** Call once on app start. Sets up interval + focus/visibility triggered re-checks. */
export function startVersionWatcher() {
    if (pollTimer) return;
    pollTimer = setInterval(checkVersion, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkVersion();
    });
    window.addEventListener('focus', checkVersion);
}
